// The navmesh's debug view: the walkable surface as a translucent fill lifted a few centimetres, its triangle edges,
// the tile grid, and optionally the crowd's agents (a ring at each radius, a line along each path). three.js is a
// peer; the game adds the group to its scene and turns it on and off as a named debug view.
import * as THREE from "three";
import type { NavCrowd } from "./crowd";
import type { NavWorld } from "./navmesh";

/** Colours and lift for the debug view (the game's look; these are defaults). */
export interface NavDebugStyle {
  fill: THREE.ColorRepresentation;
  edge: THREE.ColorRepresentation;
  grid: THREE.ColorRepresentation;
  agent: THREE.ColorRepresentation;
  path: THREE.ColorRepresentation;
  /** Metres above the mesh, so the fill doesn't fight the ground. */
  lift: number;
  opacity: number;
}

const STYLE: NavDebugStyle = { fill: "#2f9bd6", edge: "#0d3c5c", grid: "#ffd34d", agent: "#ff5a8a", path: "#ffffff", lift: 0.06, opacity: 0.45 };

/** The debug view: `group` to add to a scene; `update()` redraws the crowd's agents and paths (call per frame while on). */
export interface NavDebugView {
  group: THREE.Group;
  update(): void;
  dispose(): void;
}

/** Build the navmesh's debug view (and the crowd's, when one is given). */
export function createNavDebugView(world: NavWorld, o: { crowd?: NavCrowd; style?: Partial<NavDebugStyle> } = {}): NavDebugView {
  const st = { ...STYLE, ...o.style };
  const group = new THREE.Group();
  group.name = "nav-debug";
  const { positions, indices } = world.debugTriangles();
  const lifted = Float32Array.from(positions, (v, i) => (i % 3 === 1 ? v + st.lift : v));
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(lifted, 3));
  geo.setIndex(indices);
  const fill = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: st.fill, transparent: true, opacity: st.opacity, depthWrite: false, side: THREE.DoubleSide }));
  fill.name = "nav-debug:fill";
  fill.renderOrder = 10;
  group.add(fill);
  const edges = new THREE.LineSegments(new THREE.WireframeGeometry(geo), new THREE.LineBasicMaterial({ color: st.edge, transparent: true, opacity: 0.8 }));
  edges.name = "nav-debug:edges";
  edges.renderOrder = 11;
  group.add(edges);

  // the tile grid at the mesh's mean height
  let meanY = 0;
  for (let i = 1; i < positions.length; i += 3) meanY += positions[i]!;
  meanY = positions.length ? meanY / (positions.length / 3) + st.lift * 2 : 0;
  const g = world.grid;
  const lines: number[] = [];
  const x0 = g.origin[0];
  const z0 = g.origin[2];
  for (let i = 0; i <= g.across; i++) lines.push(x0 + i * g.tileMetres, meanY, z0, x0 + i * g.tileMetres, meanY, z0 + g.down * g.tileMetres);
  for (let j = 0; j <= g.down; j++) lines.push(x0, meanY, z0 + j * g.tileMetres, x0 + g.across * g.tileMetres, meanY, z0 + j * g.tileMetres);
  const gridGeo = new THREE.BufferGeometry();
  gridGeo.setAttribute("position", new THREE.Float32BufferAttribute(lines, 3));
  const grid = new THREE.LineSegments(gridGeo, new THREE.LineBasicMaterial({ color: st.grid }));
  grid.name = "nav-debug:tiles";
  group.add(grid);

  // the crowd: rings and paths, rebuilt into one line buffer each update
  const MAX = 4096;
  const agentGeo = new THREE.BufferGeometry();
  agentGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(MAX * 6), 3));
  agentGeo.setDrawRange(0, 0);
  const agents = new THREE.LineSegments(agentGeo, new THREE.LineBasicMaterial({ color: st.agent }));
  agents.name = "nav-debug:agents";
  agents.frustumCulled = false;
  group.add(agents);
  const pathGeo = new THREE.BufferGeometry();
  pathGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(MAX * 6), 3));
  pathGeo.setDrawRange(0, 0);
  const paths = new THREE.LineSegments(pathGeo, new THREE.LineBasicMaterial({ color: st.path }));
  paths.name = "nav-debug:paths";
  paths.frustumCulled = false;
  group.add(paths);

  const update = (): void => {
    if (!o.crowd) return;
    const a = agentGeo.getAttribute("position") as THREE.BufferAttribute;
    const p = pathGeo.getAttribute("position") as THREE.BufferAttribute;
    let na = 0;
    let np = 0;
    const seg = (attr: THREE.BufferAttribute, n: number, x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): number => {
      if (n >= MAX) return n;
      attr.setXYZ(n * 2, x0, y0, z0);
      attr.setXYZ(n * 2 + 1, x1, y1, z1);
      return n + 1;
    };
    for (const s of o.crowd.agents()) {
      const [x, y, z] = s.pos;
      const r = s.spec.radius;
      const yy = y + st.lift * 3;
      for (let i = 0; i < 12; i++) {
        const a0 = (i / 12) * Math.PI * 2;
        const a1 = ((i + 1) / 12) * Math.PI * 2;
        na = seg(a, na, x + Math.cos(a0) * r, yy, z + Math.sin(a0) * r, x + Math.cos(a1) * r, yy, z + Math.sin(a1) * r);
      }
      na = seg(a, na, x, yy, z, x + s.vel[0] * 0.5, yy, z + s.vel[2] * 0.5);
      if (s.goal.kind === "point" && s.path.length > 1) {
        let px = x;
        let pz = z;
        for (let i = s.pathAt; i < s.path.length; i++) {
          const c = s.path[i]!;
          np = seg(p, np, px, yy, pz, c[0], c[1] + st.lift * 3, c[2]);
          px = c[0];
          pz = c[2];
        }
      }
    }
    agentGeo.setDrawRange(0, na * 2);
    pathGeo.setDrawRange(0, np * 2);
    a.needsUpdate = true;
    p.needsUpdate = true;
  };
  update();
  return {
    group,
    update,
    dispose() {
      group.traverse((c) => {
        const m = c as THREE.Mesh;
        m.geometry?.dispose();
        (m.material as THREE.Material | undefined)?.dispose();
      });
    },
  };
}
